import {
  preserveClippingSourceMasks,
  type CustomTemplate,
  type CustomTemplateBox,
} from '@lite-workspace/dto-fotoclass-templates';
import type { BookCoverData, BookData } from '../interfaces/book-data.interface';
import type { CollageData } from '../interfaces/collage-data.interface';
import type { SpreadData } from '../interfaces/spread-data.interface';
import type { DrawableData, DrawableImageData, DrawableObjectData } from '../interfaces/drawable-data.interface';
import { createNormalizerContext } from './boxes/codec-contract';
import type { NormalizerContext } from './boxes/codec-contract';
import { encode as encodeImage, encodeFrameSibling } from './boxes/image-box';
import { encode as encodeText } from './boxes/text-box';
import { encode as encodeObject } from './boxes/object-box';
import { encode as encodeSmartObject } from './boxes/smart-object-box';
import { encode as encodeBackground } from './boxes/background-box';
import { encode as encodeGroup } from './boxes/group-box';
import { encode as encodeVector } from './boxes/vector-box';
import { encode as encodeAdjustment } from './boxes/adjustment-box';
import { encode as encodeUnsupported } from './boxes/unsupported-box';
import { encodeSpreadBackground } from './spread-background';
import type { NormalizeIssue, PersistedBookModelV1, PersistedCollageV1, PersistedSpreadV1 } from './types';

/** Dispatch by drawable.type — the ONLY place that knows which codec owns which OLD-side variant. */
function encodeDrawableBox(drawable: DrawableData, ctx: NormalizerContext): CustomTemplateBox {
  switch (drawable.type) {
    case 'image':
      return encodeImage(drawable, ctx);
    case 'text':
      return encodeText(drawable, ctx);
    case 'object':
      // Hybrid de-escalation (design §3 #3) — objectMeta.boxType tells apart a
      // demoted BoxType.SMART_OBJECT from a genuine BoxType.OBJECT sharing the same drawable shape.
      return drawable.objectMeta?.boxType === 'smartObject' ? encodeSmartObject(drawable, ctx) : encodeObject(drawable, ctx);
    case 'background':
      return encodeBackground(drawable, ctx);
    case 'group':
      return encodeGroup(drawable, ctx);
    case 'vector':
      return encodeVector(drawable, ctx);
    case 'adjustment':
      return encodeAdjustment(drawable, ctx);
    case 'unsupported':
      return encodeUnsupported(drawable, ctx);
    default: {
      const exhaustive: never = drawable;
      return ctx.fail(`unreachable drawable.type "${(exhaustive as DrawableData).type}"`);
    }
  }
}

/**
 * Two shapes of the same pairing. The codec mints the frame with `id === '${image.id}__frame'`
 * (`decodeFrameSibling`), but product generation gives it a random short id and records the link in
 * `idFromTemplate`, keyed by the IMAGE's own template id
 * (`photobook-shared/spread-operations/shared-box-converters.ts::generateDrawableImageFrame`). Matching on
 * `id` alone left every product-generated frame unpaired: it went out as a standalone OBJECT box and the
 * image box lost its `frameUrl`/`frameWindow`.
 */
function isFrameSiblingOf(image: DrawableImageData, candidate: DrawableData | undefined): candidate is DrawableObjectData {
  if (candidate === undefined || candidate.type !== 'object') return false;
  if (candidate.id === `${image.id}__frame`) return true;

  const link = candidate.idFromTemplate;
  if (link === undefined) return false;
  return link === `${image.id}__frame` || (image.idFromTemplate !== undefined && link === `${image.idFromTemplate}__frame`);
}

/**
 * §3 #12 — an image + its `${id}__frame` neighbor collapse back into ONE box (frameUrl/frameFileId/
 * frameWindow live on the image box's params, not as a separate persisted box).
 */
function encodeCollageBoxes(drawableObjects: readonly DrawableData[], ctx: NormalizerContext): readonly CustomTemplateBox[] {
  const boxes: CustomTemplateBox[] = [];
  let index = 0;
  while (index < drawableObjects.length) {
    const drawable = drawableObjects[index];
    const boxCtx = ctx.child(boxes.length);

    if (drawable.type === 'image') {
      const next = drawableObjects[index + 1];
      const frame = isFrameSiblingOf(drawable, next) ? next : undefined;
      const box = encodeImage(drawable, boxCtx);
      boxes.push({ ...box, params: { ...box.params, ...encodeFrameSibling(drawable, frame) } });
      index += frame ? 2 : 1;
      continue;
    }

    boxes.push(encodeDrawableBox(drawable, boxCtx));
    index += 1;
  }

  return boxes;
}

function encodeCollage(collage: CollageData, ctx: NormalizerContext): PersistedCollageV1 {
  const { drawableObjects, ...rest } = collage;
  const template = collage.originalCustomTemplate as Partial<CustomTemplate> | null;
  const boxes = encodeCollageBoxes(drawableObjects, ctx.child('boxes'));
  return { ...rest, boxes: preserveClippingSourceMasks(boxes, template?.originalLayerTree) };
}

function encodeSpreadCommon(spread: SpreadData, ctx: NormalizerContext): PersistedSpreadV1 {
  const { collages, background, ...rest } = spread;
  const collagesCtx = ctx.child('collages');
  return {
    ...rest,
    collages: collages.map((collage, i) => encodeCollage(collage, collagesCtx.child(i))),
    background: encodeSpreadBackground(background, ctx.child('background')),
  };
}

function encodeCover(cover: BookCoverData, ctx: NormalizerContext): PersistedBookModelV1['cover'] {
  return { ...encodeSpreadCommon(cover, ctx), type: cover.type };
}

function createEncodeRootContext(onIssue: (issue: NormalizeIssue) => void): NormalizerContext {
  const ctx: NormalizerContext = createNormalizerContext({
    onIssue,
    encodeDrawable: drawable => encodeDrawableBox(drawable, ctx.child(drawable.id)),
    // Shared with the top-level collage encode — a group's own `drawableObjects` gets the same
    // image+`${id}__frame` collapse, not the single-drawable dispatch.
    encodeDrawableList: drawables => encodeCollageBoxes(drawables, ctx),
  });
  return ctx;
}

/**
 * Spread-level v0→v1 — the seam a v1-native store re-enters after a legacy-typed helper produced a
 * `SpreadData`. Same codec as `normalize`, scoped to one spread.
 */
export function normalizeSpread(spread: SpreadData): { model: PersistedSpreadV1; issues: readonly NormalizeIssue[] } {
  const issues: NormalizeIssue[] = [];
  const rootCtx = createEncodeRootContext(issue => issues.push(issue));
  return { model: encodeSpreadCommon(spread, rootCtx.child('spread')), issues };
}

export function normalize(book: BookData): { model: PersistedBookModelV1; issues: readonly NormalizeIssue[] } {
  const issues: NormalizeIssue[] = [];
  const rootCtx = createEncodeRootContext(issue => issues.push(issue));

  const { cover, spreads, ...rest } = book;
  const spreadsCtx = rootCtx.child('spreads');
  const model: PersistedBookModelV1 = {
    ...rest,
    schemaVersion: 'custom-template-v1',
    cover: encodeCover(cover, rootCtx.child('cover')),
    spreads: spreads.map((spread, i) => encodeSpreadCommon(spread, spreadsCtx.child(i))),
  };

  return { model, issues };
}

export function normalizeAndLog(book: BookData, log: (issue: NormalizeIssue) => void): PersistedBookModelV1 {
  const { model, issues } = normalize(book);
  issues.forEach(log);
  return model;
}
